import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'
import { PlanType } from '@/utils/planType'

/**
 * 契約者コースプラン取得
 */
import { ApiTaTp030 } from '~/endpoints/ApiTaTp030'

export interface CoursePlanData {
  courseId: string
  planId: string
}

export interface StateContractCoursePlan {
  courseId: string
  planId: string
  planType: PlanType
  levelId: string
  coursePlanId: string
  statusCode: number
  error: {
    messageId: string
    message: string
  } | null
}
const getEmptyStateContractCoursePlan = (): StateContractCoursePlan => ({
  courseId: '',
  planId: '',
  planType: PlanType.None,
  levelId: '',
  coursePlanId: '',
  statusCode: 0,
  error: null,
})

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/ContractCoursePlan',
})
export default class ContractCoursePlan extends VuexModule {
  // States
  private state_: StateContractCoursePlan = getEmptyStateContractCoursePlan()

  // Getters

  get result(): StateContractCoursePlan {
    return this.state_
  }

  get coursePlanData(): CoursePlanData {
    return {
      courseId: this.state_.courseId,
      planId: this.state_.planId,
    }
  }

  // ログインしたプランがTコースまたはA本科コースの場合、チュートリアル動画を表示する
  get isShowTutorial(): boolean {
    return this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType.isAHonkaCourse()
  }

  // ログインしたプランがTコースまたはA本科トータルプランの場合、「カレンダー」ボタンを表示する
  get isShowCalendar(): boolean {
    return this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType === PlanType.TotalGuidancePlan
  }

  // ログインしたプランがTコースまたはNコースまたはA本科コースの場合、「学び強化ラボ」ボタンを表示する
  get isShowLab(): boolean {
    return (
      this.state_.planType === PlanType.JuniorTabletCourse ||
      this.state_.planType.isNHonkaCourse() ||
      this.state_.planType.isNSenkaCourse() ||
      this.state_.planType.isAHonkaCourse()
    )
  }

  // ログインしたプランがTコースまたはA本科コースの場合、「学習」ボタンを表示する
  get isShowLearn(): boolean {
    return this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType.isAHonkaCourse()
  }

  // ログインしたプランがTコースまたはA本科トータルプランの場合、「今月の予定」ボタンを表示する
  get isShowMonthDataList(): boolean {
    return this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType === PlanType.TotalGuidancePlan
  }

  // N本科・N専科・A専科のコース・プラン情報を持つ場合、「しつもん」ボタンを表示する
  get isShowQAndA(): boolean {
    return (
      this.state_.planType.isNHonkaCourse() ||
      this.state_.planType.isNSenkaCourse() ||
      this.state_.planType.isASenkaCourse()
    )
  }

  // ログインしたプランがTコースまたはA本科コースの場合、「ぷちぽ」ボタンを表示する
  get isShowPuchipo(): boolean {
    return this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType.isAHonkaCourse()
  }

  // ログインしたプランがTコースまたはA本科コースの場合、「記録」ボタンを表示する
  get isShowRecord(): boolean {
    return this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType.isAHonkaCourse()
  }

  // T・A本科・N本科・N専科・A専科のコース・プラン情報を持つ場合、新努力賞ポイントへの遷移ボタンを表示する
  get isShowReward(): boolean {
    // NOTE: 実質的に、2025年度中はすべてのユーザーが努力賞ポイントを利用できるため、常にtrueを返すのと同義。
    const isTAHonka = this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType.isAHonkaCourse()
    const isNASenka =
      this.state_.planType.isNHonkaCourse() ||
      this.state_.planType.isNSenkaCourse() ||
      this.state_.planType.isASenkaCourse()
    return isTAHonka || isNASenka
  }

  // コース・プランID
  get coursePlanId(): string {
    return this.state_.courseId + this.state_.planId
  }

  get isACourse(): boolean {
    return this.state_.planType.isAHonkaCourse()
  }

  // ログインしたプランがTコースまたはN本科コースの場合、「Zプロ」ボタンを表示する
  get isShowZpro(): boolean {
    return this.state_.planType === PlanType.JuniorTabletCourse || this.state_.planType.isNHonkaCourse()
  }

  // ログインしたコースがT56またはN56の場合、「オンラインスピーキング」ボタンを表示する
  get isShowOnlineSpeaking(): boolean {
    return (
      this.state_.courseId === 'T5' ||
      this.state_.courseId === 'T6' ||
      this.state_.courseId === 'N5' ||
      this.state_.courseId === 'N6'
    )
  }

  // Mutations

  @Mutation
  updateContractCoursePlan(value: StateContractCoursePlan) {
    this.state_ = value
  }

  static readonly MUTATION_NAME_UPDATE_STATE = 'updateContractCoursePlan'

  // Actions

  @Action
  clear() {
    this.context.commit(ContractCoursePlan.MUTATION_NAME_UPDATE_STATE, getEmptyStateContractCoursePlan())
  }

  @Action
  async getContractionCoursePlan(id: string): Promise<boolean> {
    logger.trace('in: getContractionCoursePlan')
    const api = new ApiTaTp030.Api()

    // API
    let hasError = true
    const result = await api.get<StateContractCoursePlan>(
      {
        query: {
          planId: id,
        },
      },
      // Success
      (data, status) => {
        hasError = false
        return {
          courseId: data.courseId,
          planId: data.planId,
          planType: PlanType.convertPlanType(data.planId),
          levelId: data.levelId,
          coursePlanId: data.coursePlanId,
          statusCode: status,
          error: null,
        }
      },
      // Error
      (data, status) => {
        hasError = true
        return {
          courseId: getEmptyStateContractCoursePlan().courseId,
          planId: getEmptyStateContractCoursePlan().planId,
          planType: getEmptyStateContractCoursePlan().planType,
          levelId: getEmptyStateContractCoursePlan().levelId,
          coursePlanId: getEmptyStateContractCoursePlan().coursePlanId,
          statusCode: status,
          error: {
            messageId: data.error.messageId,
            message: data.error.message,
          },
        }
      }
    )

    if (result) {
      this.context.commit(ContractCoursePlan.MUTATION_NAME_UPDATE_STATE, result)
    } else {
      hasError = true
    }

    return !hasError
  }
}
